/**
 * stripePayment.ts - Stripe Elements (Payment Element) integration.
 *
 * Flow:
 *   1. initStripePayment() posts the current registration draft to
 *      /register/init, gets back a client_secret sized to the cart
 *      total, and mounts the Payment Element into #StripePaymentElement.
 *   2. confirmStripePayment() is called on submit; it confirms the
 *      PaymentIntent with Stripe and returns the PaymentIntent id to
 *      include in the final /Tournament/RegisterSubmit call.
 *
 * Card/wallet details never pass through our own server - Stripe.js
 * collects and tokenizes them directly.
 */
import { CONFIG } from './config';
import { getCsrfToken } from './utils';
import { buildRegisterInitRequest } from './registrationData';
import type { RegisterInitResponse } from './types';

const STRIPE_JS_URL = 'https://js.stripe.com/v3/';
const MOUNT_SELECTOR = '#StripePaymentElement';
const ERROR_SELECTOR = '#StripePaymentErrors';

let stripe: any = null;
let elements: any = null;
let paymentElement: any = null;
let mounted = false;

function loadStripeJs(): Promise<void> {
    return new Promise((resolve, reject) => {
        if ((window as any).Stripe) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('Failed to load Stripe.js')));
            return;
        }
        const script = document.createElement('script');
        script.src = STRIPE_JS_URL;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Stripe.js'));
        document.head.appendChild(script);
    });
}

function showError(message: string): void {
    const errorEl = document.querySelector(ERROR_SELECTOR);
    if (errorEl) errorEl.textContent = message;
}

function clearError(): void {
    showError('');
}

/**
 * Creates/refreshes a PaymentIntent for the current cart and mounts the
 * Payment Element. Safe to call again (e.g. re-entering step 3 after
 * changing players/donation) - it tears down any existing element first.
 */
export async function initStripePayment(): Promise<void> {
    clearError();

    try {
        await loadStripeJs();

        if (!stripe) {
            stripe = (window as any).Stripe(window.Cr.options.stripekey);
        }

        const response = await fetch(CONFIG.registerInitUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': getCsrfToken()
            },
            body: JSON.stringify(buildRegisterInitRequest())
        });

        if (!response.ok) {
            throw new Error('Could not start payment. Please check your entries and try again.');
        }

        const data: RegisterInitResponse = await response.json();

        teardownStripePayment();

        elements = stripe.elements({ clientSecret: data.clientSecret });
        paymentElement = elements.create('payment');

        const mountPoint = document.querySelector(MOUNT_SELECTOR);
        if (mountPoint) {
            paymentElement.mount(MOUNT_SELECTOR);
            mounted = true;
        }
    } catch (err) {
        console.error('Stripe init failed:', err);
        showError(err instanceof Error ? err.message : 'Could not load the payment form.');
    }
}

export function teardownStripePayment(): void {
    if (paymentElement && mounted) {
        paymentElement.unmount();
    }
    paymentElement = null;
    elements = null;
    mounted = false;
}

/**
 * Confirms the current PaymentIntent with Stripe. Throws with a
 * user-facing message on failure/cancellation.
 * Returns the confirmed PaymentIntent id on success.
 */
export async function confirmStripePayment(): Promise<string> {
    if (!stripe || !elements) {
        throw new Error('Payment form is not ready yet. Please wait a moment and try again.');
    }

    clearError();

    const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
    });

    if (error) {
        const message = error.message || 'Payment could not be completed.';
        showError(message);
        throw new Error(message);
    }

    if (!paymentIntent || (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing')) {
        const message = 'Payment was not completed. Please try again.';
        showError(message);
        throw new Error(message);
    }

    return paymentIntent.id;
}